import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

// Skeleton loader for charts
export const ChartSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-4 bg-slate-200 rounded w-1/3 mb-2"></div>
    <div className="h-3 bg-slate-100 rounded w-1/4 mb-4"></div>
    <div className="h-56 bg-slate-100 rounded"></div>
  </div>
);

// Chart Card Wrapper
export const ChartCard = ({ title, subtitle, children, className = '', loading = false }) => (
  <div className={`bg-white rounded-xl border border-slate-200 p-5 ${className}`}>
    {loading ? (
      <ChartSkeleton />
    ) : (
      <>
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {children}
      </>
    )}
  </div>
);

// 2x2 Grid Container
export const ChartGrid = ({ children }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    {children}
  </div>
);

// Custom Tooltip for Recharts
export const CustomTooltip = ({ active, payload, label, prefix = '₹', suffix = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 text-white px-3 py-2 rounded-lg shadow-xl text-xs">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color || '#fff' }}>
            {entry.name}: {prefix}{typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}{suffix}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Color palette for charts
export const CHART_COLORS = {
  primary: '#10b981',    // emerald
  secondary: '#0ea5e9',  // cyan
  tertiary: '#8b5cf6',   // violet
  warning: '#f59e0b',    // amber
  danger: '#ef4444',     // red
  neutral: '#64748b',    // slate
  
  // Extended palette for multi-series
  palette: [
    '#10b981', '#0ea5e9', '#8b5cf6', '#f59e0b', 
    '#ef4444', '#ec4899', '#14b8a6', '#6366f1'
  ],
  
  // Status colors
  psl: '#10b981',
  nonPsl: '#64748b',
  active: '#10b981',
  dormant: '#f59e0b',
  inactive: '#ef4444',
  
  // Asset class colors
  STD: '#10b981',
  SMA: '#f59e0b',
  DB1: '#f97316',
  DB2: '#ef4444',
  DB3: '#dc2626',
};

// Format large numbers
export const formatCurrency = (value, inCrores = false) => {
  if (value === null || value === undefined) return '₹0';
  
  if (inCrores) {
    const crValue = value / 10000000;
    return `₹${crValue.toFixed(2)} Cr`;
  }
  
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)} L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(1)} K`;
  }
  return `₹${value.toLocaleString()}`;
};

// Format percentage
export const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${value.toFixed(2)}%`;
};

// Custom label renderer for Pie charts - prevents overlap
export const renderCustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name, index }) => {
  // Only show label if percentage is >= 5%
  if (percent < 0.05) return null;
  
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 25;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={500}
    >
      {`${name} ${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Custom label for outside positioning with lines
export const renderOuterPieLabel = (props) => {
  const { cx, cy, midAngle, outerRadius, percent, name } = props;
  
  // Only show label if percentage is >= 3%
  if (percent < 0.03) return null;
  
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 30;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={10}
      fontWeight={500}
    >
      {`${name} ${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Donut chart with side labels showing all values
export const DonutChartWithLabels = ({ data, colors, colorMapping, title }) => {
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
  
  return (
    <div className="flex h-full">
      {/* Left side: Labels with percentages */}
      <div className="w-36 flex flex-col justify-center pr-2 space-y-1.5 overflow-y-auto">
        {data.map((item, index) => {
          const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
          const color = colorMapping?.[item.name] || colors?.[index % (colors?.length || 8)] || CHART_COLORS.palette[index % CHART_COLORS.palette.length];
          return (
            <div key={index} className="flex items-center gap-2 text-xs">
              <div 
                className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                style={{ backgroundColor: color }}
              />
              <span className="text-slate-600 truncate flex-1" title={item.name}>
                {item.name}
              </span>
              <span className="font-semibold text-slate-800 whitespace-nowrap">
                {percent}%
              </span>
            </div>
          );
        })}
      </div>
      
      {/* Right side: Donut chart */}
      <div className="flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={65}
              paddingAngle={2}
              dataKey="value"
              labelLine={false}
            >
              {data.map((entry, index) => {
                const color = colorMapping?.[entry.name] || colors?.[index % (colors?.length || 8)] || CHART_COLORS.palette[index % CHART_COLORS.palette.length];
                return <Cell key={`cell-${index}`} fill={color} />;
              })}
            </Pie>
            <Tooltip formatter={(value) => formatCurrency(value)} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ChartCard;
